/**
 * Enum for all possible case states in the Compass system
 * These states represent the workflow status of a case record
 */
export enum CaseState {
  // Initial states
  CREATED = 'created',
  IN_PROGRESS = 'in_progress',
  PRE_PROCESSING = 'pre_processing',

  // AI states
  AI_CALL = 'ai_call',
  AI_AUDIT = 'ai_audit',

  // Scheduled states
  AI_CALL_SCHEDULED = 'ai_call_scheduled',

  // Refill workflow states (JNJ-31). REFILL_SCHEDULED is refill's equivalent of
  // AI_CALL_SCHEDULED — the parked state where the Cancel AI Call action lives —
  // and REFILL_UNRESOLVED is a terminal-ish outcome for a case that burned its
  // whole retry budget without reaching anyone.
  REFILL_AI_CALL_IN_PROGRESS = 'ai_call_in_progress',
  CALL_SCHEDULED = 'call_scheduled',
  AWAIT_TRANSFER_OUTCOME = 'awaiting_transfer_outcome',
  // Parked with no timeout after CareTend enrichment, time-zone resolution or the
  // delivery-ticket create failed past its retry cap (JNJ-172). The reason is on
  // the case error. Released by Retry / Skip; the only refill state whose input
  // and output a human may edit. Not a hermes `*_human_audit` state.
  REFILL_HUMAN_AUDIT_NEEDED = 'human_audit_needed',
  // Parked with no timeout when pre-screening finds no valid phone, no complete
  // address, no adult DOB, or several fulfillable drug orders. The reasons are on
  // the case error. Released by Mark Case as Completed once a person has called
  // the patient. Not a hermes `*_human_call` state.
  REFILL_HUMAN_CALL_NEEDED = 'human_call_needed',

  // Human intervention states
  CALL_REVIEW = 'call_review', // recall_scheduler workflow
  HUMAN_CALL = 'human_call',
  HUMAN_AUDIT = 'human_audit',
  HUMAN_STEP_MISSING_INFO = 'human_step_missing_info',
  INPUT_AUDIT_NEEDED = 'input_audit_needed',
  REACHOUT_INELIGIBLE = 'reachout_ineligible', // recall_scheduler workflow
  INELIGIBLE = 'ineligible', // referral_scheduler workflow
  PRE_REACHOUT_REVIEW = 'pre_reachout_review',
  PRE_PROCESSING_FAILED = 'pre_processing_failed',
  FAX_VERIFICATION = 'fax_verification',
  CASE_COMPLETION_REVIEW = 'case_completion_review',
  MISSING_INPUT = 'missing_input_failure',

  // Medicare eligibility check states
  PRIMARY_ELIGIBILITY_CHECK = 'primary_eligibility_check',
  PRIMARY_ELIGIBILITY_CHECK_HUMAN_AUDIT = 'primary_eligibility_check_human_audit',
  PRIMARY_HUMAN_AUDIT = 'primary_human_audit',
  PRIMARY_HUMAN_CALL = 'primary_human_call',
  PRIMARY_COMPLETED = 'primary_completed',
  PRIMARY_TERMINATED = 'primary_terminated',
  SECONDARY_ELIGIBILITY_CHECK = 'secondary_eligibility_check',
  SECONDARY_ELIGIBILITY_CHECK_HUMAN_AUDIT = 'secondary_eligibility_check_human_audit',
  SECONDARY_HUMAN_AUDIT = 'secondary_human_audit',
  SECONDARY_HUMAN_CALL = 'secondary_human_call',
  SECONDARY_COMPLETED = 'secondary_completed',
  SECONDARY_TERMINATED = 'secondary_terminated',

  // MEDBV eligibility check states (generic pattern for any workflow)
  ELIGIBILITY_CHECK_HUMAN_AUDIT = 'eligibility_check_human_audit',

  // Unified MedBV states
  ELIGIBILITY_CHECK = 'eligibility_check',
  EC_AI_AUDIT = 'ec_ai_audit',
  EC_HUMAN_AUDIT = 'ec_human_audit',
  AI_CALL_AUDIT = 'ai_call_audit',
  HUMAN_CALL_AI_AUDIT = 'human_call_ai_audit',
  MEDICARE_COVERAGE = 'medicare_coverage',

  // Workflow transition states
  CASE_CREATED = 'case_created',

  // Completion states
  COMPLETED = 'completed',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  TERMINATED = 'terminated',
}

export enum KnipperCaseState {
  // Initial states
  START = 'Start',
  CREATED = 'created',

  // AI states
  INTAKE_PROCESSING = 'intake_processing',

  // Manual states
  AWAITING_REVIEW = 'awaiting_review',

  // Human intervention states
  INTAKE_DRAFT = 'intake_draft',
  REVIEW_NEEDED = 'review_needed',

  // Completion states
  SUBMITTED = 'submitted',
  ACKNOWLEDGED = 'acknowledged',
  COMPLETED = 'completed',
  NON_REPORTABLE = 'non_reportable',
  DUPLICATE = 'duplicate',
  FAILED = 'failed',
  CANCELLED = 'cancelled',
  TERMINATED = 'terminated',
  DELIVERING_EMAIL = 'delivering_email',
}

/**
 * Manufacturer reply decision for PQC workflow.
 * Stored at `output_data.data.decision` on the case record.
 */
export enum PqcDecision {
  ACKNOWLEDGED = 'acknowledged',
  QUESTION_ASKED = 'question_asked',
  MISSING_INFO = 'missing_info',
}
